/**
 * English — the reference translation.
 *
 * This file defines the key set. `MessageKey` is derived from it and every other
 * locale is typed as `Record<MessageKey, string>`, so a missing or misspelt key
 * in any of the other twenty-two languages is a compile error rather than a
 * blank label discovered by a player.
 *
 * Placeholders are `{name}` and are substituted by `t()`. They are never
 * reordered away: a translation may move them but must not drop one, or the
 * sentence loses the number it was about.
 *
 * Money and multipliers are formatted by util/format before they arrive here.
 * They are deliberately not localised: the decimal separator has to match the
 * one the stake field accepts, and a wallet that renders differently from the
 * field it was typed into is a support ticket.
 */
export const en = {
  // --- application chrome ---
  'app.name': 'Fighter',
  'app.loadFailed': 'The game could not be loaded. Try reloading the page.',
  'app.serverUnreachable': 'Could not reach the server.',
  'app.launchMissing': 'This game link is incomplete. Please open the game again from the casino.',

  'topbar.historyTitle': 'Recent round results — click one to verify it',
  'topbar.language': 'Language',

  'hud.betRange': 'Bet range {min} – {max}',
  'hud.dailyLimit': 'Daily limit: {used} / {max}',
  'hud.commitment': '#{nonce} commitment {hash}…  ·  next #{nextNonce} {nextHash}…',
  'hud.fairness': 'Round #{nonce} · proof published in advance',
  'hud.roundTitle': 'Round #{nonce} — click to verify',

  'conn.connecting': 'Connecting…',
  'conn.reconnecting': 'Connection lost, retrying…',
  'conn.closed': 'Connection closed',

  'feed.title': 'This round',
  'feed.players': '{count} players',

  // --- the scene ---
  'stage.betsOpen': 'BETS OPEN',
  'stage.burst': '{multiplier} — BURST',
  'stage.waiting': 'WAITING FOR THE NEXT ROUND',

  // --- the two bet panels ---
  'panel.slot': 'Bet {n}',
  'panel.stake': 'Stake',
  'panel.autoCashout': 'Auto cash out (optional)',
  'panel.autoPlaceholder': 'e.g. 2.00',
  'panel.decrease': 'Halve the stake',
  'panel.increase': 'Double the stake',
  'panel.connecting': 'Connecting…',
  'panel.waiting': 'Waiting for the next round',
  'panel.bet': 'BET',
  'panel.nextRoundNote': 'for the next round',
  'panel.cancelSpent': 'SLOT CLOSED',
  'panel.cancelSpentNote': 'opens next round',
  'panel.cancel': 'CANCEL BET',
  'panel.staked': '{amount} staked',
  'panel.queued': '{amount} · waiting for the next round',
  'panel.cashOut': 'CASH OUT',
  'panel.cashedOut': 'Cashed out at {multiplier}',
  'panel.sending': 'Sending…',
  'panel.blocked': 'Play unavailable',
  'panel.lost': 'Bet lost',

  // --- notifications ---
  'toast.betCanceled': 'Bet cancelled.',
  'toast.preBetCanceled': 'Next-round bet cancelled and refunded.',
  'toast.betCanceledPending': 'Bet cancelled. Your refund is on its way to your casino balance.',
  'toast.preBetQueued': 'Staked for the next round.',
  'toast.betLost': '{multiplier} — bet lost',
  'toast.won': '{multiplier} — you won {amount}',
  'toast.wonPending': '{multiplier} — you won {amount}, being credited to your casino balance',
  'toast.noConnection': 'No connection. Reconnecting…',
  'toast.testBalance': 'Test balance added.',

  // --- fairness verification ---
  'verify.result': 'Result: {multiplier}',
  'verify.commitment': 'Commitment (published before any bet was taken):',
  'verify.seed': 'Revealed seed:',
  'verify.notFound': 'That round could not be found.',
  // --- the gate: sign in, register, blocked, limits ---
  'gate.loginTitle': 'Welcome back',
  'gate.loginSubtitle': 'Sign in to play.',
  'gate.identifier': 'Username or email',
  'gate.password': 'Password',
  'gate.signIn': 'Sign in',
  'gate.toRegister': 'No account? Register',
  'gate.registerTitle': 'Open an account',
  'gate.registerSubtitle': 'You must be 18 or over to play.',
  'gate.username': 'Username (5–24 characters)',
  'gate.email': 'Email address',
  'gate.newPassword': 'Password (at least 10 characters)',
  'gate.dateOfBirth': 'Date of birth',
  'gate.currency': 'Currency',
  'gate.countryCode': 'Country code (e.g. TR)',
  'gate.language': 'Language',
  'gate.acceptTerms':
    'I have read and accept the terms and conditions, and confirm that I am over 18.',
  'gate.register': 'Open account and sign in',
  'gate.toLogin': 'Already have an account? Sign in',
  'gate.blockedTitle': 'Play unavailable',
  'gate.devNote': 'Development environment: identity verification can be skipped.',
  'gate.devVerify': 'Approve KYC (development only)',
  'gate.retry': 'Try again',
  'gate.signOut': 'Sign out',
  'gate.faucetTitle': 'Test balance',
  'gate.faucetSubtitle': 'No payment provider is connected yet.',
  'gate.amount': 'Amount',
  'gate.addBalance': 'Add balance',
  'gate.close': 'Close',
  'gate.limitsTitle': 'Responsible gambling',
  'gate.limitsSubtitle': 'Lowering a limit applies at once; raising one takes 24 hours.',
  'gate.dailyLimit': 'Daily stake limit',
  'gate.weeklyLimit': 'Weekly stake limit',
  'gate.saveLimits': 'Save limits',
  'gate.selfExclude': 'Exclude myself (freeze my account)',
  'gate.selfExcludeDays': 'For how many days do you want to be excluded? (0 for permanent)',
  'gate.selfExcludeConfirm':
    'This cannot be undone for as long as it lasts. Type this text to confirm:',

  // --- server error codes ---
  // The code is the contract; these are its presentation. Anything unmapped
  // falls back to the server's own English message rather than being swallowed.

  // --- chrome: header, drawer, chat, history ---
  'topbar.menu': 'Menu',
  'topbar.chat': 'Chat',
  'menu.title': 'Menu',
  'menu.close': 'Close the menu',
  'menu.back': 'Back',
  'menu.fair': 'Provably fair',
  'menu.avatar': 'Avatar',
  'menu.gameLimits': 'Game limits',
  'menu.language': 'Language',
  'menu.responsible': 'Responsible gambling',
  'menu.logout': 'Sign out',
  'chat.title': 'Chat',
  'chat.soon': 'Chat is not open yet. When it is, it will be here.',
  'history.more': 'More results',
  'history.panelTitle': 'Round history',
  'history.panelHint': 'Pick a round to verify it',
  'history.loading': 'Loading…',
  'history.empty': 'No rounds yet.',
  'history.failed': 'The results could not be loaded.',


  // --- bet panel, countdown, audio ---
  'panel.tabManual': 'Bet',
  'panel.tabAuto': 'Auto',
  'panel.addSecond': 'Add a second bet',
  'panel.closeSecond': 'Close the second bet',
  'panel.closeBlockedActiveBet': 'This bet holds a stake, so it cannot be closed yet.',
  'panel.closeBlockedAutoBet': 'Turn auto bet off before closing this bet.',
  'panel.quickAmount': 'Set the stake to {amount}',
  'panel.autoBet': 'Auto bet',
  'panel.autoClear': 'Clear the auto cash-out target',
  'countdown.label': 'Next round in {seconds}s',
  'countdown.starting': 'Starting…',
  'toast.autoBetOn': 'Auto bet on — a stake goes in every round.',
  'toast.autoBetOff': 'Auto bet stopped.',
  'sound.effects': 'Sound effects',
  'sound.music': 'Music',
  'sound.failed': 'The setting could not be saved.',


  // --- provably fair page ---
  'fair.intro': 'The result of every round is fixed before betting opens, and we publish proof of it in advance. When the round ends the seed is revealed, so anyone can recompute the result. Return to player: {rtp}%.',
  'fair.round': 'Round #{nonce}',
  'fair.thisRound': 'This round',
  'fair.nextRound': 'The next round, already sealed',
  'fair.nextRoundNote': 'Published now, before anyone has bet on it.',
  'fair.lastRound': 'The last finished round',
  'fair.checking': 'Checking in your browser…',
  'fair.localPass': 'Verified here: the seed matches the commitment and produces this result.',
  'fair.localFail': 'Verification FAILED in your browser (it derives {derived}). Please contact support.',
  'fair.checkFailed': 'This browser could not run the check.',
  'fair.verifyTitle': 'Check any round',
  'fair.verifyPrompt': 'Round number',
  'fair.verifyButton': 'Check',
  'fair.badNonce': 'Enter a round number.',
  'fair.pending': 'Waiting for the first round…',
  'fair.formulaTitle': 'How it is computed',
  'fair.formula': 'h = the top 44 bits of HMAC-SHA256(key = seed, message = round number); result = floor(100 × 100 × 2^44 / (101 × h)) hundredths. The commitment is SHA-256(seed).',
  'fair.copy': 'Copy',
  'fair.copied': 'Copied',


  // --- avatar, game limits, new error codes ---
  'avatar.hint': 'Choose how you appear in the bet list.',
  'avatar.option': 'Avatar {n}',
  'avatar.saved': 'Avatar saved.',
  'avatar.failed': 'The avatar could not be saved.',
  'limits.minBet': 'Minimum bet',
  'limits.maxBet': 'Maximum bet',
  'limits.maxWin': 'Maximum win',
  'limits.note': 'These are the house limits for {currency}.',
  'limits.yourOwn': 'Your own limits are under Responsible gambling.',
  'limits.unavailable': 'The limits are not available yet.',
  'err.invalid_auto_cashout': 'The auto cash-out target must be above 1.00x.',
  'err.invalid_avatar': 'That avatar does not exist.',


  // --- audio note ---
  'sound.notYet': 'There are no sounds in the game yet. Your choice is kept for when there are.',

  'err.unauthorized': 'Your session has ended. Please sign in again.',
  'err.betting_closed': 'Betting for this round has closed.',
  'err.round_not_running': 'The round has not started yet.',
  'err.already_bet': 'That bet slot is already staked.',
  'err.no_active_bet': 'You have no active bet in that slot.',
  'err.already_cashed_out': 'That bet has already been settled.',
  'err.too_late': 'Too late — the sandbag burst.',
  'err.insufficient_funds': 'Your balance is not enough for that stake.',
  'err.stake_out_of_range': 'That stake is outside the allowed range.',
  'err.limit_exceeded': 'That stake would exceed a limit you have set.',
  'err.betting_blocked': 'Play is not available on this account at the moment.',
  'err.rate_limited': 'Too fast. Please slow down a little.',
  'err.bad_request': 'The request could not be understood.',
  'err.internal_error': 'Something went wrong. Please try again.',
  'err.maintenance': 'The game is briefly unavailable for maintenance. Please try again shortly.',
  'err.launch_expired': 'This game link has expired. Please open the game again from the casino.',
  'err.wallet_unavailable': 'The casino wallet is not responding. Please try again in a moment.',
  'err.betting_window_closing': 'The betting window is closing — place this bet on the next round.',
  'err.cancel_limit_reached': 'You have taken this bet back twice this round. This slot opens again next round.',
  'err.invalid_slot': 'That bet slot does not exist.',
  'err.next_round_unavailable': 'The next round is not open for bets yet.',
  'err.not_found': 'Not found.',
  'err.invalid_username': 'The username must be between 5 and 24 characters.',
  'err.invalid_email': 'Enter a valid email address.',
  'err.invalid_password': 'The password must be at least 10 characters.',
  'err.terms_required': 'The terms and conditions must be accepted.',
  'err.invalid_currency': 'Choose one of the supported currencies.',
  'err.invalid_date_of_birth': 'The date of birth must be in YYYY-MM-DD form.',
  'err.date_of_birth_required': 'A date of birth is required.',
  'err.invalid_country': 'Enter a two-letter country code.',
  'err.invalid_language': 'That language is not one of the supported ones.',
  'err.already_registered': 'That username or email address is already in use.',
  'err.underage': 'You must be at least 18 years old to open an account.',
  'err.no_wallet': 'No wallet has been opened for your account. Please contact support.',
  'err.confirmation_required': 'The confirmation text must be typed exactly.',
  'err.limit_only_lowered': 'Monthly and single-stake limits can only be lowered.',
  'err.unexpected': 'An unexpected error occurred.',

  // --- reasons the game may refuse to open ---
  'blocked.kyc_required': 'You need to complete identity verification before betting.',
  'blocked.kyc_rejected': 'Your identity verification was not accepted. Please contact support.',
  'blocked.self_excluded': 'Your account is self-excluded. Play is not available.',
  'blocked.account_suspended': 'Your account is suspended. Please contact support.',
  'blocked.account_closed': 'This account has been closed.',
  'blocked.game_access_restricted': 'Access to this game has been restricted on your account. Please contact support.',
  'blocked.default': 'Play is not available on this account at the moment.',
  // --- fun mode and the way back to the casino ---
  'demo.badge': 'Demo',
  'demo.reset': 'Reset play balance',
  'toast.demoReset': 'Play balance reset to {amount}.',
  'lobby.back': 'Back to the casino',

} as const;

/** Every key the interface may ask for. */
export type MessageKey = keyof typeof en;

/**
 * A complete translation. Every locale must supply every key: a partial table
 * would show a player a mixture of two languages, and the type system is a
 * cheaper place to find that out than production.
 */
export type Messages = Record<MessageKey, string>;

export default en;
